import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { AppStateContext } from '../../contexts/AppStateContext';
import {
  MARKET_INDEX_CONFIG,
  MARKET_INDEX_SYMBOLS,
} from '../../constants/marketIndices';
import {
  DEFAULT_RELATIVE_STRENGTH_SETTINGS,
  normalizeRelativeStrengthSettings,
  relativeStrengthCompositeScore,
} from '../../constants/relativeStrengthSettings';
import {
  getRsTimeframe,
  orderRsTimeframeKeys,
  RS_MAX_MULTI_TIMEFRAMES,
  RS_SINGLE_TIMEFRAMES,
} from '../../constants/relativeStrengthTimeframes';
import { rsStudyPath } from '../../constants/dashboardRoutes';
import {
  RS_SOURCE_FAVORITES,
  RS_SOURCE_WATCHLIST,
  buildWatchlistSourceOptions,
  getDefaultRsTickerSource,
  getPremadeRsWatchlist,
  isVirtualWatchlistId,
  normalizeTickerList,
  parseRsUserWatchlistId,
  resolveRsTickerSource,
  rsUserWatchlistSourceId,
} from '../../constants/watchlistDefaults';
import FavoriteStar from '../shared/FavoriteStar';
import { useFavoriteTickers } from '../../utils/favoriteTickers';
import { MOBILE_UI } from '../../constants/mobileUi';
import useApi from '../../hooks/useApi';
import { useRelativeStrength } from '../../hooks/useRelativeStrength';
import { useRelativeStrengthHistory } from '../../hooks/useRelativeStrengthHistory';
import { queryKeys, useWatchlistTickersQuery } from '../../hooks/useTradeStationQueries';
import { WatchlistService } from '../../services/watchlistService';
import { addTickerAction } from '../../state/watchlistsActions';
import { useWatchlistTickerDetails } from '../../hooks/useWatchlistTickerDetails';
import useIsMobile from '../../hooks/useIsMobile';
import BottomSheet from '../shared/BottomSheet';
import SegmentedControl from '../shared/SegmentedControl';
import { navigateToTradeWithSymbol } from '../../utils/tradeNavigation';
import { primeTickerModalKeyboard } from '../../utils/tickerModalKeyboard';
import { isBrokerLinked } from '../../utils/brokerConnection';
import {
  formatVsBenchmark,
  relativeStrengthScore,
  relativeStrengthScoreHeatColor,
  relativeStrengthTrendAlign,
} from '../../utils/relativeStrength';
import StrengthOdometer, {
  StrengthCombinedPill,
  StrengthScaleLegend,
  StrengthScanMeter,
} from './StrengthOdometer';
import RelativeStrengthMobileToolbar from './RelativeStrengthMobileToolbar';
import RelativeStrengthSkeletonRows from './RelativeStrengthSkeletonRows';
import RelativeStrengthHistoryChart from './RelativeStrengthHistoryChart';
import PositionMiniChart from '../StockChart/PositionMiniChart';
import {
  POSITION_CHART_TIMEFRAMES,
  DEFAULT_POSITION_CHART_TIMEFRAME,
} from '../../constants/positionChartTimeframes';

const CHART_INTERVAL_OPTIONS = POSITION_CHART_TIMEFRAMES.map((tf) => ({
  value: tf.value,
  label: tf.label,
}));

/** Symbol/name column: resizable; width persisted in localStorage. */
const RS_SYMBOL_COL_WIDTH_KEY = 'pt:rsSymbolColWidth';
const RS_SYMBOL_COL_DEFAULT = 320;
/** Written on mount before the default was raised; treat as unset. */
const RS_SYMBOL_COL_LEGACY_DEFAULT = 200;
const RS_SYMBOL_COL_MIN = 220;
const RS_SYMBOL_COL_MAX = 520;

function clampSymbolColWidth(raw) {
  if (raw == null || raw === '') return RS_SYMBOL_COL_DEFAULT;
  const n = Number(raw);
  if (!Number.isFinite(n)) return RS_SYMBOL_COL_DEFAULT;
  return Math.max(RS_SYMBOL_COL_MIN, Math.min(RS_SYMBOL_COL_MAX, Math.round(n)));
}

function readStoredSymbolColWidth() {
  try {
    const raw = localStorage.getItem(RS_SYMBOL_COL_WIDTH_KEY);
    if (raw == null || raw === '' || Number(raw) === RS_SYMBOL_COL_LEGACY_DEFAULT) {
      return RS_SYMBOL_COL_DEFAULT;
    }
    return clampSymbolColWidth(raw);
  } catch (_) {
    return RS_SYMBOL_COL_DEFAULT;
  }
}

const RS_CHART_HEIGHT_KEY = 'pt:rsChartHeight';
const RS_CHART_HEIGHT_DEFAULT = 220;
const RS_CHART_HEIGHT_MIN = 140;
const RS_CHART_HEIGHT_MAX = 520;

function clampChartHeight(raw) {
  if (raw == null || raw === '') return RS_CHART_HEIGHT_DEFAULT;
  const n = Number(raw);
  if (!Number.isFinite(n)) return RS_CHART_HEIGHT_DEFAULT;
  return Math.max(RS_CHART_HEIGHT_MIN, Math.min(RS_CHART_HEIGHT_MAX, Math.round(n)));
}

function readStoredChartHeight() {
  try {
    return clampChartHeight(localStorage.getItem(RS_CHART_HEIGHT_KEY));
  } catch (_) {
    return RS_CHART_HEIGHT_DEFAULT;
  }
}

function RelativeStrengthExpandedChart({
  ticker,
  height,
  interval,
  unit,
  colors,
  isMobile,
  onResizeStart,
}) {
  return (
    <div
      data-testid={`relative-strength-chart-${ticker}`}
      data-height={String(height)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        position: 'relative',
        padding: '0 12px 2px',
      }}
    >
      <div style={{ height, minHeight: height }}>
        <PositionMiniChart
          symbol={ticker}
          height={height}
          fillHeight
          hideMeta
          interval={interval}
          unit={unit}
        />
      </div>
      {isMobile ? null : (
        <div
          data-testid={`relative-strength-chart-resize-${ticker}`}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize chart"
          onMouseDown={onResizeStart}
          style={{
            height: 14,
            margin: '0 -12px -2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'row-resize',
            touchAction: 'none',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 36,
              height: 3,
              borderRadius: 999,
              backgroundColor: colors.border,
            }}
          />
        </div>
      )}
    </div>
  );
}

function buildSingleGrid(symbolWidthPx) {
  // Ticker/name absorbs leftover width so Score / vs stay compact on the right.
  return `minmax(${symbolWidthPx}px, 1fr) 72px 100px 72px`;
}

function buildMultiGrid(symbolWidthPx, multiKeys) {
  const scoreCols = (multiKeys || []).map(() => '120px').join(' ');
  return `minmax(${symbolWidthPx}px, 1fr) ${scoreCols} 56px`;
}

/**
 * Same columns as desktop, sized to a phone: the symbol column flexes instead of
 * taking the persisted desktop width, so every column fits at 360px.
 */
function buildMobileSingleGrid() {
  return 'minmax(72px, 1fr) 72px minmax(56px, auto) 48px';
}

function buildMobileMultiGrid(multiKeys) {
  const scoreCols = (multiKeys || []).map(() => '56px').join(' ');
  return `minmax(72px, 1fr) ${scoreCols} 48px`;
}

function formatStrengthScore(score) {
  if (!Number.isFinite(score)) return '—';
  return String(score);
}

function tickerButtonStyle(colors) {
  return {
    border: 'none',
    background: 'transparent',
    padding: 0,
    fontWeight: 700,
    color: colors.primary,
    cursor: 'pointer',
    fontSize: 13,
    textDecoration: 'none',
    textAlign: 'left',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

function TickerIdentity({ ticker, companyName, colors, onOpenTrade, favorited, onToggleFavorite }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        width: '100%',
        gap: 2,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <button
          type="button"
          data-testid={`relative-strength-ticker-${ticker}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpenTrade(ticker);
          }}
          style={tickerButtonStyle(colors)}
          title={`Open ${ticker} on Trade`}
        >
          {ticker}
        </button>
        <FavoriteStar ticker={ticker} favorited={favorited} onToggle={onToggleFavorite} />
      </div>
      {companyName ? (
        <span
          data-testid={`relative-strength-name-${ticker}`}
          title={companyName}
          style={{
            display: 'block',
            width: '100%',
            fontSize: 11,
            color: colors.textMuted,
            opacity: 0.85,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {companyName}
        </span>
      ) : null}
    </div>
  );
}

function MethodologyModal({ show, onHide, benchmark, colors, isMobile }) {
  const sym = String(benchmark || 'SPY').toUpperCase();
  const benchName = MARKET_INDEX_CONFIG[sym]?.name || sym;
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  useEffect(() => {
    if (!show) setDisclaimerOpen(false);
  }, [show]);

  const formulaLabel = `Score equals the sum of volume times (stock percent minus ${sym} percent), divided by the sum of volume`;

  const body = (
    <>
      <div style={{ marginBottom: 12 }}>
        Relative strength helps identify stocks that are outperforming or underperforming{' '}
        <strong>{benchName} ({sym})</strong> by accounting for price and volume.
      </div>

      <div style={{ color: colors.textMuted, marginBottom: 14 }}>
        We compare each stock to {sym} over the time you pick. The score is 0–100.
        {' '}
        <strong style={{ color: colors.text }}>50</strong>
        {' '}
        is even with {sym}. Higher is stronger. Lower is weaker.
      </div>

      <div style={{ fontWeight: 700, marginBottom: 4 }}>How the score is built</div>
      <div style={{ color: colors.textMuted, marginBottom: 12 }}>
        For each bar, we measure how much the stock beat (or lost to) {sym}.
        {' '}
        Stock +3%, {sym} +1% → <strong style={{ color: colors.text }}>+2%</strong>.
        {' '}
        Then we weight that by the bar’s volume so higher-volume bars count more.
      </div>

      <div
        data-testid="relative-strength-methodology-formula"
        style={{
          marginBottom: 14,
          padding: '14px 12px 12px',
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.bgSecondary || colors.bgPrimary,
          color: colors.text,
        }}
      >
        <div
          role="img"
          aria-label={formulaLabel}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.2,
          }}
        >
          <span style={{ fontWeight: 700 }}>Score</span>
          <span aria-hidden>=</span>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
            <div style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
              Σ volume × (stock% − {sym}%)
            </div>
            <div
              aria-hidden
              style={{
                width: '100%',
                height: 1,
                margin: '5px 0',
                backgroundColor: colors.text,
                opacity: 0.55,
              }}
            />
            <div>Σ volume</div>
          </div>
        </div>
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            lineHeight: 1.4,
            color: colors.textMuted,
            textAlign: 'center',
          }}
        >
          stock% and {sym}% are that bar’s percent change.
        </div>
      </div>

      <div style={{ color: colors.textMuted, marginBottom: 14 }}>
        A low-volume drift and a high-volume surge can look the same on a simple percentage.
        The score tells them apart because it checks whether the move happened on real volume.
      </div>

      <div style={{ fontWeight: 700, marginBottom: 4 }}>Combined</div>
      <div style={{ color: colors.textMuted, marginBottom: 16 }}>
        The Combined column on the right is a weighted average of the timeframe scores
        on that row. Longer windows count more than shorter ones. A stock that is only
        strong on 15m ranks below one that is strong on Today or Month too.
      </div>

      <div
        style={{
          marginTop: 4,
          paddingTop: 12,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <button
          type="button"
          data-testid="relative-strength-methodology-disclaimer"
          aria-expanded={disclaimerOpen}
          onClick={() => setDisclaimerOpen((open) => !open)}
          style={{
            display: 'block',
            width: '100%',
            minHeight: isMobile ? MOBILE_UI.touchMin : 28,
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: colors.textMuted,
            fontSize: 12,
            fontWeight: 600,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          Disclaimer
        </button>
        {disclaimerOpen ? (
          <div
            data-testid="relative-strength-methodology-disclaimer-body"
            style={{
              marginTop: 8,
              fontSize: 12,
              lineHeight: 1.5,
              color: colors.textMuted,
            }}
          >
            These scores are for information only. They are not investment advice, a solicitation,
            or a recommendation to buy or sell any security. Past performance is not indicative of
            future results. You can lose money. Do not invest or trade based solely on relative
            strength. PrecisionTrader is not a registered investment adviser. All trading decisions
            are yours, at your own risk.
            {' '}
            <Link
              to="/legal#risk"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="relative-strength-methodology-disclaimer-link"
              style={{
                color: colors.primary,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Risk disclosure
            </Link>
          </div>
        ) : null}
      </div>
    </>
  );

  const gotItButton = (
    <button
      type="button"
      data-testid="relative-strength-methodology-close"
      onClick={onHide}
      style={{
        height: isMobile ? MOBILE_UI.touchMin : 32,
        width: isMobile ? '100%' : undefined,
        padding: '0 14px',
        borderRadius: 8,
        border: 'none',
        backgroundColor: colors.primary,
        color: '#fff',
        fontWeight: 600,
        fontSize: 13,
        cursor: 'pointer',
      }}
    >
      Got it
    </button>
  );

  if (isMobile) {
    return (
      <BottomSheet
        show={show}
        onHide={onHide}
        title="How relative strength works"
        testId="relative-strength-methodology-modal"
        closeLabel="Close methodology"
        maxHeight="min(76vh, 720px)"
        footer={gotItButton}
      >
        <div style={{ color: colors.text, fontSize: 13, lineHeight: 1.5 }}>
          {body}
        </div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      animation={false}
      data-testid="relative-strength-methodology-modal"
    >
      <Modal.Header
        closeButton
        closeVariant="white"
        style={{
          backgroundColor: colors.bgSecondary || colors.bgCard,
          borderBottom: `1px solid ${colors.border}`,
          color: colors.text,
        }}
      >
        <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>
          How relative strength works
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          backgroundColor: colors.bgCard,
          color: colors.text,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        {body}
      </Modal.Body>
      <Modal.Footer
        style={{
          backgroundColor: colors.bgSecondary || colors.bgCard,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        {gotItButton}
      </Modal.Footer>
    </Modal>
  );
}

function scoreFromRow(row) {
  if (!row?.ok || row.loading) return null;
  return relativeStrengthScore(row.scorePct);
}

/**
 * Relative strength / weakness table for the Relative Strength page.
 * Filter chips pick the list, benchmark, sort, and display. The purple plus adds a ticker to that list.
 */
export default function RelativeStrengthPanel({
  timeframeKey = 'today',
  onTimeframeChange,
  settings: settingsProp,
  onSettingsChange,
  relativeToOptions = MARKET_INDEX_SYMBOLS,
  relativeTo,
  onRelativeToChange,
  listMaxHeight = 310,
  showTimeframe = true,
  fillHeight = false,
  methodologyOpen = null,
  onMethodologyOpenChange = null,
}) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { selectedAccount, positions } = useContext(TradingContext);
  const {
    watchlists = [],
    watchlistTickers,
    watchlistTickersLoading,
    selectedWatchlistId,
    addWatchlistTicker,
  } = useContext(AppStateContext);
  const queryClient = useQueryClient();
  const { request } = useApi();
  const watchlistApi = useMemo(() => WatchlistService(request), [request]);
  const { favorites, toggleFavorite, isFavorite } = useFavoriteTickers();

  const settings = useMemo(
    () => normalizeRelativeStrengthSettings(settingsProp || DEFAULT_RELATIVE_STRENGTH_SETTINGS),
    [settingsProp],
  );
  const isMulti = settings.mode === 'multi';
  const multiKeys = useMemo(
    () => orderRsTimeframeKeys(settings.multiTimeframes),
    [settings.multiTimeframes],
  );
  const tfA = multiKeys[0] || 'today';
  const tfB = multiKeys[1] || tfA;
  const tfC = multiKeys[2] || tfA;
  const tfD = multiKeys[3] || tfA;

  const [sortDirection, setSortDirection] = useState('strength');
  /** Multi mode: which score column to sort — Combined or a selected timeframe key. */
  const [sortColumn, setSortColumn] = useState('comp');
  const [symbolColWidth, setSymbolColWidth] = useState(readStoredSymbolColWidth);
  const symbolResizeRef = useRef({ active: false, startX: 0, startWidth: RS_SYMBOL_COL_DEFAULT });
  const [chartHeight, setChartHeight] = useState(readStoredChartHeight);
  const chartResizeRef = useRef({ active: false, startY: 0, startHeight: RS_CHART_HEIGHT_DEFAULT });
  const [extraTickers, setExtraTickers] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [expandedTicker, setExpandedTicker] = useState(null);
  const [chartTimeframeKey, setChartTimeframeKey] = useState(DEFAULT_POSITION_CHART_TIMEFRAME);
  const selectedChartTimeframe = useMemo(
    () => POSITION_CHART_TIMEFRAMES.find((tf) => tf.value === chartTimeframeKey)
      || POSITION_CHART_TIMEFRAMES.find((tf) => tf.value === DEFAULT_POSITION_CHART_TIMEFRAME)
      || POSITION_CHART_TIMEFRAMES[0],
    [chartTimeframeKey],
  );
  const [uncontrolledMethodology, setUncontrolledMethodology] = useState(false);
  const methodologyControlled = typeof onMethodologyOpenChange === 'function';
  const showMethodology = methodologyControlled ? !!methodologyOpen : uncontrolledMethodology;
  const setShowMethodology = methodologyControlled ? onMethodologyOpenChange : setUncontrolledMethodology;
  const defaultTickerSource = getDefaultRsTickerSource({ brokerLinked: isBrokerLinked(user) });
  const [tickerSource, setTickerSource] = useState(() => {
    try {
      return localStorage.getItem('pt:rsTickerSource') || defaultTickerSource;
    } catch (_) {
      return defaultTickerSource;
    }
  });

  const userWatchlistId = parseRsUserWatchlistId(tickerSource);
  const isPremadeSource = Boolean(getPremadeRsWatchlist(tickerSource));
  const isFavoritesSource = tickerSource === RS_SOURCE_FAVORITES;
  const isCurrentWatchlistSource = tickerSource === RS_SOURCE_WATCHLIST
    || (userWatchlistId != null && String(userWatchlistId) === String(selectedWatchlistId));
  const needsRemoteWatchlistTickers = Boolean(
    userWatchlistId
    && userWatchlistId !== 'positions'
    && !isCurrentWatchlistSource
    && !isPremadeSource
    && !isFavoritesSource,
  );

  const remoteWatchlistTickersQuery = useWatchlistTickersQuery(
    needsRemoteWatchlistTickers ? userWatchlistId : null,
    needsRemoteWatchlistTickers,
  );

  useEffect(() => {
    setExtraTickers([]);
  }, [tickerSource]);

  const sourceTickers = useMemo(() => {
    let base;
    if (isFavoritesSource) {
      base = resolveRsTickerSource(RS_SOURCE_FAVORITES, favorites);
    } else if (isPremadeSource) {
      base = resolveRsTickerSource(tickerSource, []);
    } else if (userWatchlistId === 'positions') {
      base = normalizeTickerList((positions || []).map((p) => p.Symbol));
    } else if (isCurrentWatchlistSource || tickerSource === RS_SOURCE_WATCHLIST) {
      base = resolveRsTickerSource(RS_SOURCE_WATCHLIST, watchlistTickers);
    } else if (needsRemoteWatchlistTickers) {
      base = normalizeTickerList(remoteWatchlistTickersQuery.data || []);
    } else {
      base = resolveRsTickerSource(RS_SOURCE_WATCHLIST, watchlistTickers);
    }
    if (!extraTickers.length) return base;
    const seen = new Set(base);
    const extras = extraTickers.filter((ticker) => !seen.has(ticker));
    return extras.length ? [...base, ...extras] : base;
  }, [
    isFavoritesSource,
    favorites,
    isPremadeSource,
    tickerSource,
    userWatchlistId,
    positions,
    isCurrentWatchlistSource,
    watchlistTickers,
    needsRemoteWatchlistTickers,
    remoteWatchlistTickersQuery.data,
    extraTickers,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem('pt:rsTickerSource', tickerSource);
    } catch (_) {}
  }, [tickerSource]);

  useEffect(() => {
    if (!isMulti) return;
    if (sortColumn === 'comp') return;
    if (!multiKeys.includes(sortColumn)) setSortColumn('comp');
  }, [isMulti, multiKeys, sortColumn]);

  useEffect(() => {
    if (tickerSource === RS_SOURCE_WATCHLIST && selectedWatchlistId != null) {
      setTickerSource(rsUserWatchlistSourceId(selectedWatchlistId));
      return;
    }
    if (!userWatchlistId || isPremadeSource || isFavoritesSource) return;
    if (!watchlists?.length) return;
    const exists = watchlists.some((w) => String(w.id) === String(userWatchlistId));
    if (!exists) setTickerSource(getDefaultRsTickerSource({ brokerLinked: isBrokerLinked(user) }));
  }, [tickerSource, selectedWatchlistId, userWatchlistId, isPremadeSource, isFavoritesSource, watchlists, user]);

  const historyTimeframe = settings.historyTimeframe || 'month';
  const history = useRelativeStrengthHistory({
    tickers: sourceTickers,
    relativeTo,
    timeframeKey: historyTimeframe,
    enabled: Boolean(relativeTo) && sourceTickers.length > 0,
  });

  const rsBase = {
    tickers: sourceTickers,
    relativeTo,
    sortDirection,
  };

  const resultA = useRelativeStrength({
    ...rsBase,
    timeframeKey: isMulti ? tfA : timeframeKey,
    enabled: Boolean(relativeTo),
  });
  const resultB = useRelativeStrength({
    ...rsBase,
    timeframeKey: tfB,
    enabled: isMulti && multiKeys.length > 1 && Boolean(relativeTo),
  });
  const resultC = useRelativeStrength({
    ...rsBase,
    timeframeKey: tfC,
    enabled: isMulti && multiKeys.length > 2 && Boolean(relativeTo),
  });
  const resultD = useRelativeStrength({
    ...rsBase,
    timeframeKey: tfD,
    enabled: isMulti && multiKeys.length > 3 && Boolean(relativeTo),
  });

  const multiResults = useMemo(() => {
    const list = [resultA];
    if (multiKeys.length > 1) list.push(resultB);
    if (multiKeys.length > 2) list.push(resultC);
    if (multiKeys.length > 3) list.push(resultD);
    return list;
  }, [resultA, resultB, resultC, resultD, multiKeys.length]);

  const { loading, error, rows, lookupResult } = useMemo(() => {
    if (!isMulti) {
      return {
        loading: resultA.loading,
        error: resultA.error,
        rows: resultA.rows,
        lookupResult: resultA.lookupResult,
      };
    }

    const byTicker = new Map();
    const ensure = (ticker) => {
      if (!byTicker.has(ticker)) {
        byTicker.set(ticker, {
          ticker,
          scores: multiKeys.map(() => null),
          loadingFlags: multiKeys.map(() => false),
          okFlags: multiKeys.map(() => false),
          isLookup: false,
        });
      }
      return byTicker.get(ticker);
    };

    multiResults.forEach((result, tfIdx) => {
      (result.rows || []).forEach((row) => {
        const entry = ensure(row.ticker);
        entry.scores[tfIdx] = scoreFromRow(row);
        entry.loadingFlags[tfIdx] = Boolean(row.loading);
        entry.okFlags[tfIdx] = Boolean(row.ok);
        if (row.isLookup) entry.isLookup = true;
      });
      if (result.lookupResult?.ticker) {
        const entry = ensure(result.lookupResult.ticker);
        entry.scores[tfIdx] = scoreFromRow(result.lookupResult);
        entry.loadingFlags[tfIdx] = Boolean(result.lookupResult.loading);
        entry.okFlags[tfIdx] = Boolean(result.lookupResult.ok);
        entry.isLookup = true;
      }
    });

    let merged = Array.from(byTicker.values()).map((entry) => {
      const loadingAny = entry.loadingFlags.some(Boolean);
      const ok = entry.okFlags.some(Boolean);
      const composite = relativeStrengthCompositeScore(entry.scores, multiKeys);
      return {
        ticker: entry.ticker,
        ok,
        loading: loadingAny && !ok,
        scores: entry.scores,
        score100: composite,
        isLookup: entry.isLookup,
      };
    });

    merged.sort((a, b) => {
      const scoreForSort = (row) => {
        if (sortColumn !== 'comp') {
          const idx = multiKeys.indexOf(sortColumn);
          if (idx >= 0) {
            const tfScore = row.scores?.[idx];
            return Number.isFinite(tfScore) ? tfScore : null;
          }
        }
        return Number.isFinite(row.score100) ? row.score100 : null;
      };
      const aRaw = scoreForSort(a);
      const bRaw = scoreForSort(b);
      const aScore = aRaw != null ? aRaw : (sortDirection === 'strength' ? -Infinity : Infinity);
      const bScore = bRaw != null ? bRaw : (sortDirection === 'strength' ? -Infinity : Infinity);
      return sortDirection === 'strength' ? bScore - aScore : aScore - bScore;
    });

    return {
      loading: multiResults.some((r) => r.loading),
      error: multiResults.find((r) => r.error)?.error || null,
      rows: merged,
      lookupResult: null,
    };
  }, [isMulti, resultA, multiResults, multiKeys, sortDirection, sortColumn]);

  const detailTickers = useMemo(
    () => rows.map((r) => ({ ticker: r.ticker })),
    [rows],
  );
  const { getTickerDetails } = useWatchlistTickerDetails(detailTickers);

  const highlight = useMemo(() => {
    if (selectedTicker) {
      return rows.find((r) => r.ticker === selectedTicker) || lookupResult;
    }
    return lookupResult || rows[0] || null;
  }, [selectedTicker, rows, lookupResult]);

  const openTrade = useCallback((ticker) => {
    navigateToTradeWithSymbol(navigate, ticker, user, selectedAccount, isMobile ? {
      backTo: `${location.pathname}${location.search || ''}`,
      backTitle: 'Relative Strength',
    } : undefined);
  }, [navigate, user, selectedAccount, isMobile, location.pathname, location.search]);

  const openAddTicker = useCallback(() => {
    primeTickerModalKeyboard();
    window.dispatchEvent(new CustomEvent('ticker:add-to-watchlist', {
      detail: {
        onAdd: async (ticker) => {
          const sym = String(ticker || '').trim().toUpperCase();
          if (!sym) return;
          setSelectedTicker(sym);
          setExtraTickers((prev) => (prev.includes(sym) ? prev : [...prev, sym]));
          const canPersist = Boolean(
            user?.token
            && userWatchlistId
            && !isVirtualWatchlistId(userWatchlistId),
          );
          if (!canPersist) return;
          try {
            if (String(userWatchlistId) === String(selectedWatchlistId) && addWatchlistTicker) {
              await addWatchlistTicker(sym);
              return;
            }
            const current = Array.isArray(remoteWatchlistTickersQuery.data)
              ? remoteWatchlistTickersQuery.data
              : [];
            const setRemoteTickers = (updater) => {
              queryClient.setQueryData(
                queryKeys.watchlistTickers(userWatchlistId, user?.id),
                (prev) => (
                  typeof updater === 'function'
                    ? updater(Array.isArray(prev) ? prev : [])
                    : updater
                ),
              );
            };
            await addTickerAction(
              watchlistApi,
              userWatchlistId,
              sym,
              current,
              setRemoteTickers,
              null,
              queryClient,
            );
          } catch (err) {
            setExtraTickers((prev) => prev.filter((item) => item !== sym));
            throw err;
          }
        },
      },
    }));
  }, [
    addWatchlistTicker,
    queryClient,
    remoteWatchlistTickersQuery.data,
    selectedWatchlistId,
    user,
    userWatchlistId,
    watchlistApi,
  ]);

  const sourceOptions = useMemo(() => buildWatchlistSourceOptions(watchlists), [watchlists]);

  const relativeToSelectOptions = useMemo(
    () => relativeToOptions.map((sym) => ({
      value: sym,
      label: MARKET_INDEX_CONFIG[sym]?.name || sym,
    })),
    [relativeToOptions],
  );

  const sortColumnOptions = useMemo(() => ([
    { value: 'comp', label: 'Combined' },
    ...multiKeys.map((key) => ({
      value: key,
      label: getRsTimeframe(key).label,
    })),
  ]), [multiKeys]);

  const sourceLoading = (
    (isCurrentWatchlistSource && watchlistTickersLoading)
    || (needsRemoteWatchlistTickers && remoteWatchlistTickersQuery.isLoading)
  );
  const isColdLoading = (loading || sourceLoading) && rows.length === 0;
  const isUserListSource = Boolean(userWatchlistId) || tickerSource === RS_SOURCE_WATCHLIST;
  const timeframeOptions = useMemo(
    () => RS_SINGLE_TIMEFRAMES.map((tf) => ({ value: tf.key, label: tf.label })),
    [],
  );

  const singleGrid = useMemo(
    () => (isMobile ? buildMobileSingleGrid() : buildSingleGrid(symbolColWidth)),
    [isMobile, symbolColWidth],
  );

  const multiGrid = useMemo(
    () => (isMobile ? buildMobileMultiGrid(multiKeys) : buildMultiGrid(symbolColWidth, multiKeys)),
    [isMobile, symbolColWidth, multiKeys],
  );

  const gridGap = isMobile ? 10 : 14;

  useEffect(() => {
    try {
      localStorage.setItem(RS_SYMBOL_COL_WIDTH_KEY, String(symbolColWidth));
    } catch (_) { /* ignore */ }
  }, [symbolColWidth]);

  useEffect(() => {
    try {
      localStorage.setItem(RS_CHART_HEIGHT_KEY, String(chartHeight));
    } catch (_) { /* ignore */ }
  }, [chartHeight]);

  const onSymbolResizeStart = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    symbolResizeRef.current = {
      active: true,
      startX: event.clientX,
      startWidth: symbolColWidth,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [symbolColWidth]);

  useEffect(() => {
    const onMove = (event) => {
      if (!symbolResizeRef.current.active) return;
      const delta = event.clientX - symbolResizeRef.current.startX;
      setSymbolColWidth(clampSymbolColWidth(symbolResizeRef.current.startWidth + delta));
    };
    const onUp = () => {
      if (!symbolResizeRef.current.active) return;
      symbolResizeRef.current.active = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const onChartResizeStart = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    chartResizeRef.current = {
      active: true,
      startY: event.clientY,
      startHeight: chartHeight,
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, [chartHeight]);

  useEffect(() => {
    const onMove = (event) => {
      if (!chartResizeRef.current.active) return;
      const delta = event.clientY - chartResizeRef.current.startY;
      setChartHeight(clampChartHeight(chartResizeRef.current.startHeight + delta));
    };
    const onUp = () => {
      if (!chartResizeRef.current.active) return;
      chartResizeRef.current.active = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const symbolHeaderCell = (
    <span
      data-testid="relative-strength-col-symbol"
      style={{ position: 'relative', display: 'block', minWidth: 0, paddingRight: 6 }}
    >
      Symbol
      {isMobile ? null : (
        <span
          data-testid="relative-strength-symbol-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize symbol column"
          onMouseDown={onSymbolResizeStart}
          style={{
            position: 'absolute',
            top: -10,
            right: -8,
            bottom: -10,
            width: 12,
            cursor: 'col-resize',
            zIndex: 2,
          }}
        />
      )}
    </span>
  );

  const handleModeChange = useCallback((mode) => {
    if (settings.mode === mode) return;
    onSettingsChange?.({ ...settings, mode });
  }, [settings, onSettingsChange]);

  const handleMultiTimeframeToggle = useCallback((key) => {
    const next = new Set(multiKeys);
    if (next.has(key)) {
      if (next.size <= 1) return;
      next.delete(key);
    } else if (next.size < RS_MAX_MULTI_TIMEFRAMES) {
      next.add(key);
    } else {
      return;
    }
    onSettingsChange?.({ ...settings, multiTimeframes: orderRsTimeframeKeys([...next]) });
  }, [multiKeys, settings, onSettingsChange]);

  return (
    <div
      data-testid="relative-strength-panel"
      style={!isMobile && fillHeight ? {
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      } : undefined}
    >
      <MethodologyModal
        show={showMethodology}
        onHide={() => setShowMethodology(false)}
        benchmark={relativeTo}
        colors={colors}
        isMobile={isMobile}
      />

      <div
        data-testid="relative-strength-card"
        data-flat="true"
        style={isMobile ? {
          padding: 0,
          borderRadius: 0,
          border: 'none',
          backgroundColor: 'transparent',
          overflow: 'visible',
        } : {
          padding: 0,
          borderRadius: 0,
          border: 'none',
          backgroundColor: 'transparent',
          overflow: 'visible',
          ...(fillHeight ? {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          } : null),
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            minWidth: 0,
            marginBottom: isMobile ? 0 : 8,
          }}
        >
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <RelativeStrengthMobileToolbar
          sourceOptions={sourceOptions}
          tickerSource={tickerSource}
          onTickerSourceChange={setTickerSource}
          onAddTicker={openAddTicker}
          relativeTo={relativeTo}
          relativeToSelectOptions={relativeToSelectOptions}
          onRelativeToChange={onRelativeToChange}
          sortDirection={sortDirection}
          onSortDirectionToggle={() => setSortDirection((d) => (d === 'strength' ? 'weakness' : 'strength'))}
          isMulti={isMulti}
          onModeChange={handleModeChange}
          showTimeframe={showTimeframe}
          timeframeKey={timeframeKey}
          timeframeOptions={timeframeOptions}
          onTimeframeChange={(next) => onTimeframeChange?.(next)}
          multiKeys={multiKeys}
          onMultiTimeframeToggle={handleMultiTimeframeToggle}
          sortColumn={sortColumn}
          sortColumnOptions={sortColumnOptions}
          onSortColumnChange={setSortColumn}
          onShowMethodology={() => setShowMethodology(true)}
        />
        </div>
        {!isMobile ? <StrengthScaleLegend benchmark={relativeTo} /> : null}
        {user?.superuser ? (
          <Link
            to={rsStudyPath({
              source: tickerSource,
              benchmark: relativeTo,
              timeframes: isMulti ? multiKeys : [timeframeKey],
              tickers: sourceTickers,
              threshold: 60,
            })}
            data-testid="relative-strength-study-link"
            style={{ fontSize: 12, color: colors.primary, whiteSpace: 'nowrap', textDecoration: 'none' }}
          >
            RS Study
          </Link>
        ) : null}
        </div>

        {error && (
          <div style={{ color: colors.danger, fontSize: 13, marginBottom: 10 }}>{error}</div>
        )}

        {expandedTicker ? (
          <div
            data-testid="relative-strength-chart-interval"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <SegmentedControl
              options={CHART_INTERVAL_OPTIONS}
              value={chartTimeframeKey}
              onChange={setChartTimeframeKey}
              size="sm"
              scrollable
              ariaLabel="Chart interval"
              testId="relative-strength-chart-timeframe"
            />
          </div>
        ) : null}
        <div
          data-testid="relative-strength-list"
          data-mode={isMulti ? 'multi' : 'single'}
          style={isMobile ? {
            border: 'none',
            borderRadius: 0,
          } : {
            maxHeight: fillHeight ? undefined : listMaxHeight,
            flex: fillHeight ? 1 : undefined,
            minHeight: fillHeight ? 0 : undefined,
            overflowY: 'auto',
            border: 'none',
            borderRadius: 0,
          }}
        >
          {isMulti ? (
            <div
              data-testid="relative-strength-list-header"
              style={{
                display: 'grid',
                gridTemplateColumns: multiGrid,
                alignItems: 'center',
                gap: gridGap,
                padding: '8px 12px 6px',
                color: colors.textMuted,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.02,
                textTransform: 'none',
                borderBottom: 'none',
                backgroundColor: 'transparent',
              }}
            >
              {symbolHeaderCell}
              {multiKeys.map((key) => (
                <span
                  key={key}
                  data-testid={`relative-strength-col-tf-${key}`}
                  style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                >
                  {getRsTimeframe(key).label}
                </span>
              ))}
              <span
                data-testid="relative-strength-col-comp"
                style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
              >
                {isMobile ? 'Comp' : 'Combined'}
              </span>
            </div>
          ) : (
            <div
              data-testid="relative-strength-list-header"
              style={{
                display: 'grid',
                gridTemplateColumns: singleGrid,
                alignItems: 'center',
                gap: gridGap,
                padding: '8px 12px 6px',
                color: colors.textMuted,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.02,
                textTransform: 'none',
                borderBottom: 'none',
                backgroundColor: 'transparent',
              }}
            >
              {symbolHeaderCell}
              <span data-testid="relative-strength-col-meter" aria-hidden="true" />
              <span data-testid="relative-strength-col-score" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Score (0-100)</span>
              <span data-testid="relative-strength-col-vs" style={{ textAlign: 'right' }}>
                {`vs ${relativeTo}`}
              </span>
            </div>
          )}
          {isColdLoading ? (
            <RelativeStrengthSkeletonRows
              gridTemplateColumns={isMulti ? multiGrid : singleGrid}
              gap={gridGap}
            />
          ) : null}
          {!isColdLoading && rows.length === 0 && (
            <div style={{ padding: 14, color: colors.textMuted, fontSize: 13 }}>
              {sourceTickers.length === 0
                ? (isFavoritesSource
                  ? 'Star tickers to add them to Favorites.'
                  : isUserListSource
                    ? 'Add tickers to this list to rank relative strength.'
                    : 'This list has no tickers.')
                : 'Not enough overlapping data yet for this timeframe.'}
            </div>
          )}
          {rows.map((row) => {
            const active = highlight?.ticker === row.ticker;
            const expanded = expandedTicker === row.ticker;
            const details = getTickerDetails(row.ticker);
            const companyName = details?.Description || details?.Root || '';

            if (isMulti) {
              return (
                <div
                  key={row.ticker}
                  data-testid={`relative-strength-row-${row.ticker}`}
                  data-expanded={expanded ? 'true' : 'false'}
                  onClick={() => {
                    setSelectedTicker(row.ticker);
                    setExpandedTicker((prev) => (prev === row.ticker ? null : row.ticker));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedTicker(row.ticker);
                      setExpandedTicker((prev) => (prev === row.ticker ? null : row.ticker));
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  style={{
                    borderBottom: `1px solid ${colors.border}`,
                    backgroundColor: active ? `${colors.primary}14` : 'transparent',
                    boxShadow: active ? `inset 2px 0 0 ${colors.primary}` : 'none',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    data-testid={`relative-strength-row-grid-${row.ticker}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: multiGrid,
                      alignItems: 'center',
                      gap: gridGap,
                      padding: '10px 12px',
                    }}
                  >
                    <TickerIdentity
                      ticker={row.ticker}
                      companyName={companyName}
                      colors={colors}
                      onOpenTrade={openTrade}
                      favorited={isFavorite(row.ticker)}
                      onToggleFavorite={toggleFavorite}
                    />
                    {multiKeys.map((key, idx) => {
                      const score = row.scores?.[idx];
                      return (
                        <StrengthScanMeter
                          key={key}
                          testId={`relative-strength-score-${row.ticker}-${key}`}
                          score100={score}
                          loading={row.loading && !Number.isFinite(score)}
                        />
                      );
                    })}
                    <div
                      data-testid={`relative-strength-comp-${row.ticker}`}
                      data-score={Number.isFinite(row.score100) ? String(Math.round(row.score100)) : ''}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        minWidth: 0,
                      }}
                    >
                      <StrengthCombinedPill
                        score100={row.score100}
                        align={relativeStrengthTrendAlign(row.score100)}
                        loading={row.loading && !Number.isFinite(row.score100)}
                        colors={colors}
                      />
                    </div>
                  </div>
                  {expanded ? (
                    <RelativeStrengthExpandedChart
                      ticker={row.ticker}
                      height={chartHeight}
                      interval={selectedChartTimeframe.interval}
                      unit={selectedChartTimeframe.unit}
                      colors={colors}
                      isMobile={isMobile}
                      onResizeStart={onChartResizeStart}
                    />
                  ) : null}
                </div>
              );
            }

            const volumePct = row.ok ? row.scorePct : null;
            const vsPct = row.ok ? row.windowExcessPct : null;
            const strengthScore = relativeStrengthScore(volumePct);
            const vsCompact = formatVsBenchmark(vsPct, relativeTo, { compact: true });
            return (
              <div
                key={row.ticker}
                data-testid={`relative-strength-row-${row.ticker}`}
                data-expanded={expanded ? 'true' : 'false'}
                onClick={() => {
                  setSelectedTicker(row.ticker);
                  setExpandedTicker((prev) => (prev === row.ticker ? null : row.ticker));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedTicker(row.ticker);
                    setExpandedTicker((prev) => (prev === row.ticker ? null : row.ticker));
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                style={{
                  borderBottom: `1px solid ${colors.border}`,
                  backgroundColor: active ? `${colors.primary}14` : 'transparent',
                  boxShadow: active ? `inset 2px 0 0 ${colors.primary}` : 'none',
                  cursor: 'pointer',
                }}
              >
              <div
                data-testid={`relative-strength-row-grid-${row.ticker}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: singleGrid,
                  alignItems: 'center',
                  gap: gridGap,
                  padding: '10px 12px',
                }}
              >
                <TickerIdentity
                  ticker={row.ticker}
                  companyName={companyName}
                  colors={colors}
                  onOpenTrade={openTrade}
                  favorited={isFavorite(row.ticker)}
                  onToggleFavorite={toggleFavorite}
                />
                <StrengthOdometer
                  size="mini"
                  scorePct={volumePct}
                  score100={strengthScore}
                  loading={Boolean(row.loading)}
                  title={row.loading ? undefined : formatVsBenchmark(vsPct, relativeTo)}
                />
                <div
                  data-testid={`relative-strength-score-${row.ticker}`}
                  title={
                    row.loading
                      ? undefined
                      : `Volume-weighted score · ${formatVsBenchmark(vsPct, relativeTo)}`
                  }
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'right',
                    color: row.loading
                      ? colors.textMuted
                      : relativeStrengthScoreHeatColor(strengthScore, colors),
                  }}
                >
                  {row.loading ? '—' : formatStrengthScore(strengthScore)}
                </div>
                <div
                  data-testid={`relative-strength-vs-${row.ticker}`}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'right',
                    color: row.loading
                      ? colors.textMuted
                      : (vsPct >= 0 ? colors.success : colors.danger),
                  }}
                >
                  {row.loading ? '—' : vsCompact}
                </div>
              </div>
              {expanded ? (
                <RelativeStrengthExpandedChart
                  ticker={row.ticker}
                  height={chartHeight}
                  interval={selectedChartTimeframe.interval}
                  unit={selectedChartTimeframe.unit}
                  colors={colors}
                  isMobile={isMobile}
                  onResizeStart={onChartResizeStart}
                />
              ) : null}
              </div>
            );
          })}
        </div>
        <div
          style={{
            flex: '0 0 auto',
            marginTop: isMobile ? 16 : 8,
            paddingTop: 16,
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          <RelativeStrengthHistoryChart
            tickers={sourceTickers}
            seriesByTicker={history.seriesByTicker}
            averageRs={history.averageRs}
            groupIndex={history.groupIndex}
            benchmarkIndex={history.benchmarkIndex}
            benchmark={relativeTo}
            timeframeKey={historyTimeframe}
            onTimeframeChange={(next) => onSettingsChange?.({ ...settings, historyTimeframe: next })}
            highlightedTicker={expandedTicker || selectedTicker}
            loading={history.loading}
            error={history.error}
            height={isMobile ? 360 : 440}
          />
        </div>
      </div>
    </div>
  );
}
